import {observer} from "mobx-react-lite";
import React, {useEffect, useMemo} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Tag} from "primereact/tag";

import clubStore from "../../storage/ClubStore";
import appStore from "../../storage/AppStore";
import systemStore from "../../storage/SystemStore";
import Btn from "../../ui/Btn";
import PageLayout from "../PageLayout";
import NumericPagination from "../NumericPagination";

import "../../common/billing.css";
import "../../common/game.css";

const GameBilling = () => {
  const navigate = useNavigate();
  const {id} = useParams();
  const gameId = Number(id);

  useEffect(() => {
    if (!gameId) return;

    clubStore.tournament.getGame(gameId);

    clubStore.billing.query.setProperty("company_id", clubStore.club.id);
    clubStore.billing.query.setProperty("billing_type_id", 3);
    clubStore.billing.query.setProperty("object_id", gameId);
    clubStore.billing.query.setProperty("company_user_id", 0);
    clubStore.billing.query.setProperty("page_number", 1);

    clubStore.billing.search();
  }, [gameId]);

  const game = clubStore.tournament.game;

  const unpaidItems = useMemo(
    () => (clubStore.billing.items || []).filter((x) => !x.is_paid),
    [clubStore.billing.items]
  );

  const totalDebt = useMemo(() => {
    return (clubStore.billing.items || [])
      .filter(x => !x.is_paid)
      .reduce((sum, x) => sum + Number(x.amount || 0), 0)
      .toFixed(2);
  }, [clubStore.billing.items]);

  const hasAnyBilling = (clubStore.billing.items || []).length > 0;

  const getSeverity = (is_paid, is_approved) => {
    if (!is_paid) return "danger";
    if (!is_approved) return "warning";
    return "success";
  };

  const BillingItem = ({bill}) => {
    const paid = bill.is_paid;
    const approved = bill.is_approved;

    return (
      <div className="game-card h-full flex flex-column">
        <div className="flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
          <div className="flex align-items-start gap-3 flex-1 min-w-0">
            <div className="competition-form__section-icon flex-shrink-0">
              <i className="pi pi-user" aria-hidden="true"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold line-height-3 text-lg">{bill.company_user?.title}</div>
              <div className="text-sm text-color-secondary mt-2 line-height-3">
                {bill.billing_type?.name}
                {bill.object?.name && `, ${bill.object.name}`}
                {bill.description && <span>, {bill.description}</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-column gap-2 align-items-end flex-shrink-0">
            <div className={`font-bold text-xl ${paid ? "text-green-600" : "text-red-600"}`}>
              {bill.amount} {bill.currency?.sign}
            </div>
            <div className="flex align-items-center flex-wrap justify-content-end gap-2">
              <Tag severity={getSeverity(paid, approved)}>
                {paid ? appStore.tr.paid : appStore.tr.unpaid}
              </Tag>
              {paid && (
                <Tag severity="info">
                  {bill.payment_method === "cash" ? appStore.tr.cash : appStore.tr.transfer}
                </Tag>
              )}
            </div>
          </div>
        </div>

        {bill.notes && (
          <div className="competition-detail-item text-sm line-height-3 mb-3">
            <i className="pi pi-comment text-color-secondary mr-2" aria-hidden="true"/>
            {bill.notes}
          </div>
        )}

        <div className="game-card-actions flex-wrap justify-content-end align-items-center mt-auto pt-3 border-top-1 surface-border">
          <Btn icon="pi pi-eye" severity="success" outlined
            onClick={() => navigate(`/manager/billing/detail/${bill.id}`)}/>
          {!bill.is_paid && (
            <Btn icon="pi pi-send" severity="warning" outlined label={appStore.tr.inform}
              onClick={() => {
                clubStore.billing.informGameBilling(gameId, bill.id).then(() => systemStore.setSuccess(appStore.tr.saved_successfully));
              }}/>
          )}
        </div>
      </div>
    );
  };

  return (
    <PageLayout
      header={
        <div className="w-full flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="flex align-items-center gap-3 min-w-0 flex-1">
            <span className="competition-form__page-icon flex-shrink-0">
              <i className="pi pi-wallet" aria-hidden="true"/>
            </span>
            <h3 className="m-0 line-height-3 min-w-0">
              {game?.name || appStore.tr.billing} ({clubStore.billing.query.total || 0})
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn icon="pi pi-plus" severity="success" label={appStore.tr.generate || "Generate"}
              onClick={async () => {
                await clubStore.billing.generateGameBilling(gameId);
                await clubStore.billing.search();
              }}/>
            {hasAnyBilling && unpaidItems.length > 0 && (
              <Btn icon="pi pi-send" severity="warning" label={appStore.tr.inform || "Inform"}
                onClick={() => clubStore.billing.informGameBilling(gameId, null).then(() => systemStore.setSuccess(appStore.tr.saved_successfully))}/>
            )}
          </div>
        </div>
      }
      pagination={
        <NumericPagination query={clubStore.billing.query}
          onPageChange={(page) => {
            clubStore.billing.query.setProperty("page_number", page);
            clubStore.billing.search();
          }}/>
      }
    >
      <div className="col-12">
        <div className="game-page p-3 md:p-4 flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="flex flex-column gap-2 min-w-0">
            <div className="flex align-items-center gap-2 text-sm text-color-secondary">
              <i className="pi pi-calendar text-primary" aria-hidden="true"/>
              <span>{game?.start_time_display}</span>
            </div>
            {game?.address && (
              <div className="flex align-items-center gap-2 text-sm text-color-secondary">
                <i className="pi pi-map-marker text-primary" aria-hidden="true"/>
                <span>{game.address}</span>
              </div>
            )}
          </div>
          <div className="flex align-items-center gap-3 flex-wrap">
            <span className="text-sm text-color-secondary font-medium">{appStore.tr.total_debt}</span>
            <strong className="text-2xl text-red-600">{totalDebt}</strong>
            {unpaidItems.length > 0 && (
              <span className="badge badge-danger">
                <i className="pi pi-exclamation-circle" aria-hidden="true"/>
                {unpaidItems.length}
              </span>
            )}
          </div>
        </div>
      </div>

      {clubStore.billing.items.length === 0 && (
        <div className="col-12">
          <div className="game-page p-4 flex flex-column align-items-center justify-content-center gap-2 text-color-secondary">
            <i className="pi pi-inbox text-2xl" aria-hidden="true"/>
            <span>{appStore.tr.not_found}</span>
          </div>
        </div>
      )}

      {clubStore.billing.items.map((item, index) => (
        <div className="col-12 lg:col-6" key={item.id || index}>
          <BillingItem bill={item}/>
        </div>
      ))}
    </PageLayout>
  );
};

export default observer(GameBilling);
